import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackgroundScene from "../components/BackgroundScene";
import SEO from "../components/SEO";
import { usePlausible } from "../hooks/usePlausible";
import { createEventProps, CATEGORIES } from "../utils/analytics";
import styles from "../routes/Home/Home.module.css";
import { isBrowser, getNavigator } from "../utils/ssrUtils";
import { withAssetPath, buildCanonicalUrl } from "../utils/basePath";

interface DownloadProps {}

interface ReleaseInfo {
  version: string;
  releaseDate: string;
}

const Download: React.FC<DownloadProps> = () => {
  const [osType, setOsType] = useState<"windows" | "mac" | "unknown">(
    "unknown"
  );
  const [releaseInfo, setReleaseInfo] = useState<ReleaseInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const trackEvent = usePlausible();

  // Detect OS
  useEffect(() => {
    // Skip on server-side
    if (!isBrowser()) return;

    const navigator = getNavigator();
    if (!navigator) return;

    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("mac")) {
      setOsType("mac");
    } else if (userAgent.includes("win")) {
      setOsType("windows");
    } else {
      setOsType("windows");
    }
  }, []);

  // Fetch release info
  useEffect(() => {
    const fetchReleaseInfo = async () => {
      try {
        const response = await fetch(
          "https://releases.rythm.fm/releases/latest.yml"
        );
        const yamlText = await response.text();

        // Parse simple YAML for version
        const versionMatch = yamlText.match(/version:\s*(.+)/);
        const releaseDateMatch = yamlText.match(/releaseDate:\s*(.+)/);

        if (versionMatch) {
          setReleaseInfo({
            version: versionMatch[1].trim(),
            releaseDate: releaseDateMatch
              ? releaseDateMatch[1].trim()
              : new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error("Failed to fetch release info:", error);
        // Fallback version
        setReleaseInfo({
          version: "1.0.35",
          releaseDate: new Date().toISOString(),
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleaseInfo();
  }, []);



  const getDownloadUrl = () => {
    if (!releaseInfo) return "#";

    if (osType === "mac") {
      return `https://releases.rythm.fm/releases/Rythm-${releaseInfo.version}-universal.dmg`;
    } else if (osType === "windows") {
      return `https://releases.rythm.fm/releases/Rythm-${releaseInfo.version}.exe`;
    }
    return "#";
  };

  const getOsName = () => {
    switch (osType) {
      case "mac":
        return "macOS";
      case "windows":
        return "Windows";
      default:
        return "your platform";
    }
  };

  const handleDownloadClick = () => {
    trackEvent(
      "Download",
      createEventProps(
        CATEGORIES.DOWNLOAD,
        osType,
        releaseInfo?.version || "unknown"
      )
    );
  };

  if (isLoading) {
    return (
      <div
        className={styles.home}
        style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        <BackgroundScene modelScale={2.0} />
        <Header />
        <main
          className={styles.mainContent}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={styles.home}
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <SEO
        title="Download Rythm - Discord Music App"
        description="Download the Rythm desktop app for the ultimate music listening experience. Available for Windows and macOS."
        canonicalUrl={buildCanonicalUrl("/download", { forceRoot: true })}
        keywords="rythm download, discord music app, desktop app, windows, macos, free download"
        ogImage="https://rythm.fm/assets/images/download-og.png"
        ogImageAlt="Download Rythm Desktop App - Free for Windows and macOS"
        schemaType="SoftwareApplication"
      />
      <BackgroundScene modelScale={2.0} />
      <Header />
      <main
        className={styles.mainContent}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {/* Cassette video background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            top: "90%",
            left: "10%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "auto",
            zIndex: 2,
            opacity: 0.8,
            rotate: "10deg",
            pointerEvents: "none",
            objectFit: "contain",
          }}
        >
          <source src={withAssetPath("/assets/video/cassette.mp4")} type="video/mp4" />
        </video>

        

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "2rem",
            paddingTop: "max(6rem, calc(80px + 2rem))",
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <h1
            style={{
              fontSize: 30,
              fontWeight: 700,
              background:
                "linear-gradient(135deg, #7C3AED 0%,rgb(72, 80, 236) 50%,rgb(11, 73, 245) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "1rem",
              lineHeight: 1.2,
            }}
          >
            Download
          </h1>

          {/* Rythm Logo Video */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1.5rem",
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              style={{
                width: "400px",
                height: "auto",
                objectFit: "contain",
                transition: "transform 0.3s ease",
                cursor: "pointer",
                opacity: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
              onClick={() => {
                // Get download URL using the same logic as the main button
                const downloadUrl = getDownloadUrl();
                if (downloadUrl !== "#") {
                  // Track the click event
                  trackEvent(
                    "Download",
                    createEventProps(
                      CATEGORIES.DOWNLOAD,
                      `${osType}-logo-click`,
                      releaseInfo?.version || "unknown"
                    )
                  );
                  // Open download link
                  window.open(downloadUrl, '_self');
                }
              }}
            >
              <source src={withAssetPath("/assets/video/little-logo_5.webm")} type="video/webm" />
              <source src={withAssetPath("/assets/video/little-logo.mp4")} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          <p
            style={{
              fontSize: "18px",
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: "22px",
              maxWidth: "650px",
              lineHeight: 1.6,
            }}
          >
            Get the desktop app for {getOsName()} and experience music listening
            like never before
          </p>

          {osType !== "unknown" && releaseInfo && (
            <a
              href={getDownloadUrl()}
              onClick={handleDownloadClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "12px",
                padding: "1rem 2.5rem",
                background: "linear-gradient(135deg, #147AFE 0%,rgb(113, 72, 236) 100%)",
                color: "white",
                textDecoration: "none",
                borderRadius: "26px",
                fontSize: "1.2rem",
                fontWeight: 600,
                boxShadow: "0 10px 30px rgba(124, 58, 237, 0.3)",
                cursor: "pointer",
                transition: "transform 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L12 15M12 15L8 11M12 15L16 11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 17L2 19C2 20.1046 2.89543 21 4 21L20 21C21.1046 21 22 20.1046 22 19L22 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Download for {getOsName()}
            </a>
          )}

          {releaseInfo && (
            <div
              style={{
                marginTop: "2rem",
                color: "rgba(255, 255, 255, 0.4)",
                fontSize: 12,
                opacity: 1,
              }}
            >
              Version {releaseInfo.version}
            </div>
          )}

          <div
            style={{
              marginTop: "2rem",
              padding: "1.5rem",
              background: "rgba(255, 255, 255, 0.04)",
              borderRadius: "12px",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
              opacity: 1,
            }}
          >
            <p
              style={{
                color: "rgba(255, 255, 255, 0.5)",
                marginBottom: "1rem",
              }}
            >
              Wrong OS? Download below:
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <a
                href={`https://releases.rythm.fm/releases/Rythm-${
                  releaseInfo?.version || "1.0.35"
                }.exe`}
                onClick={() => handleDownloadClick()}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "rgba(255, 255, 255, 0.04)",
                  color: "rgba(255, 255, 255, 0.8)",
                  textDecoration: "none",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "background 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.04)";
                }}
              >
                Windows (.exe)
              </a>
              <a
                href={`https://releases.rythm.fm/releases/Rythm-${
                  releaseInfo?.version || "1.0.35"
                }-universal.dmg`}
                onClick={() => handleDownloadClick()}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "rgba(255, 255, 255, 0.04)",
                  color: "rgba(255, 255, 255, 0.8)",
                  textDecoration: "none",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "background 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.04)";
                }}
              >
                macOS (.dmg)
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Download;
